import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { type LayoutChangeEvent, Pressable, StyleSheet } from "react-native";
import { Text } from "react-native-paper";

type Props = {
	text: string;
	variant?: "bodyMedium" | "bodyLarge";
	color: string;
	expandLinkColor: string;
	numberOfLines?: number;
	/** When this value changes, the component resets to collapsed state */
	resetKey?: string;
};

export const ExpandableText = ({
	text,
	variant = "bodyMedium",
	color,
	expandLinkColor,
	numberOfLines = 3,
	resetKey,
}: Props) => {
	const { t } = useTranslation();
	const [expanded, setExpanded] = useState(false);
	const [isTruncated, setIsTruncated] = useState(false);
	const fullHeightRef = useRef(0);
	const clampedHeightRef = useRef(0);

	// biome-ignore lint/correctness/useExhaustiveDependencies: reset state when content identity changes
	useEffect(() => {
		setExpanded(false);
		setIsTruncated(false);
		fullHeightRef.current = 0;
		clampedHeightRef.current = 0;
	}, [resetKey]);

	const checkTruncation = () => {
		if (
			!isTruncated &&
			fullHeightRef.current > 0 &&
			clampedHeightRef.current > 0
		) {
			setIsTruncated(fullHeightRef.current > clampedHeightRef.current + 1);
		}
	};

	const onFullLayout = (e: LayoutChangeEvent) => {
		fullHeightRef.current = e.nativeEvent.layout.height;
		checkTruncation();
	};

	const onClampedLayout = (e: LayoutChangeEvent) => {
		if (!expanded) {
			clampedHeightRef.current = e.nativeEvent.layout.height;
			checkTruncation();
		}
	};

	return (
		<Pressable
			onPress={() => setExpanded((prev) => !prev)}
			disabled={!isTruncated}
			accessibilityRole="button"
			accessibilityLabel={
				expanded ? t("common.collapseA11yLabel") : t("common.expandA11yLabel")
			}
		>
			<Text
				variant={variant}
				onLayout={onFullLayout}
				style={[styles.measureText, { color }]}
			>
				{text}
			</Text>
			<Text
				variant={variant}
				numberOfLines={expanded ? undefined : numberOfLines}
				onLayout={onClampedLayout}
				style={{ color }}
			>
				{text}
			</Text>
			{isTruncated && (
				<Text
					variant="labelMedium"
					style={{
						marginTop: 4,
						color: expandLinkColor,
					}}
				>
					{expanded ? t("common.showLess") : t("common.showMore")}
				</Text>
			)}
		</Pressable>
	);
};

const styles = StyleSheet.create({
	measureText: {
		position: "absolute",
		opacity: 0,
		left: 0,
		right: 0,
		pointerEvents: "none",
	},
});
